import { Grid } from "@mui/material";
import Typography from "@mui/material/Typography";
import { hasAnyAccessToResource } from "api/general";
import { useAppSelector } from "app/hooks";
import { BadgeNameReadOnly } from "components/Badges/BadgeNameReadOnly";
import { ReactComponent as InfoCircleIcon } from "components/Icons/info-circle.svg";
import { ExternalLinkTooltipContent } from "components/Tooltips/ExternalLinkTooltip";
import { TooltipWithIcon } from "components/Tooltips/TooltipWithIcon";
import { useMessage } from "hooks/intl";
import { isUndefined } from "lodash";
import { useEffect } from "react";
import { selectDefaultLanguageData } from "reduxState/translations/translationsSlice";
import { AchievementActivity } from "types/achievements";
import { OLDate } from "utilities/dateFormat";
import { useStyles } from "./SingleAchievement.styles";

interface Props {
  achievementData?: {
    activity?: AchievementActivity;
    translations?: {
      [key: string]: {
        name: string;
        description?: string;
      };
    };
    badgeTypeId?: string;
  };
  setAchievementName?: (name: string | undefined) => void;
}

export const BasicSettings = ({
  achievementData,
  setAchievementName,
}: Props) => {
  const classes = useStyles();
  const { message } = useMessage();
  const hasAnyBadgePermission = hasAnyAccessToResource("BADGE");
  const defaultLang = useAppSelector(selectDefaultLanguageData);
  const { code, name } = defaultLang;
  const achievementName = achievementData?.translations?.[code];

  useEffect(() => {
    if (setAchievementName && defaultLang && achievementData) {
      if (achievementName) {
        setAchievementName(achievementName.name);
      }
    }
    // eslint-disable-next-line
  }, [defaultLang, achievementData]);

  const prepareDateToDisplay = (activity?: AchievementActivity) => {
    switch (activity?.operator) {
      case "is_after":
        return message({ id: "afterDate" }, { date: OLDate(activity?.data) });
      case "is_before":
        return message({ id: "beforeDate" }, { date: OLDate(activity?.data) });
      case "is_day_of_week":
      case "is_month_of_year":
        return activity?.data
          ?.map((item: string) => message({ id: item }))
          .join(", ");
      case "is_between":
        return message(
          { id: "betweenDates" },
          {
            dateFrom: OLDate(activity?.data?.from),
            dateTo: OLDate(activity?.data?.to),
          },
        );
      case "is_not_between":
        return message(
          { id: "notBetweenDates" },
          {
            dateFrom: OLDate(activity?.data?.from),
            dateTo: OLDate(activity?.data?.to),
          },
        );
      case "is_time_between":
        return message(
          { id: "betweenDates" },
          { dateFrom: activity?.data?.from, dateTo: activity?.data?.to },
        );
      case "is_day_of_month":
        return message({ id: "nthDayOfTheMonth" }, { number: activity?.data });

      default:
        return message({ id: "alwaysAvailable" });
    }
  };
  const achievementBasicSettings = [
    {
      left: "achievementNameLocale",
      leftDataTestId: "achievement-name-label",
      right: achievementName?.name,
      rightDataTestId: "achievement-name-value",
    },
    {
      left: "achievementDescriptionLocale",
      leftDataTestId: "achievement-description-label",
      right: achievementName?.description,
      rightDataTestId: "achievement-description-value",
    },
    {
      left: "timeframe",
      leftTooltip: (
        <TooltipWithIcon
          isWhite
          icon={<InfoCircleIcon />}
          text={
            <>
              <Typography className={classes.tooltipTitle}>
                {message({ id: "timeframe" })}
              </Typography>
              <Typography className={classes.tooltipContent}>
                {message({ id: "timeframeInfo" })}
              </Typography>
            </>
          }
          placement="bottom"
          className={classes.tooltipIcon}
        />
      ),
      leftDataTestId: "timeframe-label",
      right: prepareDateToDisplay(achievementData?.activity),
      rightDataTestId: "timeframe-value",
    },
    ...(hasAnyBadgePermission
      ? [
          {
            left: message({ id: "badgeName" }),
            leftTooltip: (
              <TooltipWithIcon
                isWhite
                icon={<InfoCircleIcon className={classes.tooltipIcon} />}
                text={
                  <ExternalLinkTooltipContent
                    name="badge"
                    text={message({ id: "badgesLimitHelpInfo" })}
                    externalLink="https://help.openloyalty.io/main-features/achievements/badges"
                  />
                }
              />
            ),
            right: (
              <BadgeNameReadOnly badgeTypeId={achievementData?.badgeTypeId} />
            ),
            rightDataTestId: "badge-name-value",
          },
        ]
      : []),
  ];

  return (
    <>
      <Grid container className={classes.gridContainer}>
        <Grid className={classes.gridChild} item xs={12}>
          <span className={classes.header}>
            {message({ id: "basicSettings" })}
          </span>
          {achievementBasicSettings.map((row) => (
            <span className={classes.content} key={row.left}>
              <span className={classes.left} data-test-id={row.leftDataTestId}>
                {message({ id: row.left }, { locale: name })}
                {row.leftTooltip ? row.leftTooltip : null}
              </span>
              <span
                className={classes.right}
                data-test-id={row.rightDataTestId}
              >
                {isUndefined(row.right) ? "-" : row.right}
              </span>
            </span>
          ))}
        </Grid>
      </Grid>
    </>
  );
};
